import { CascadeMenu } from '@aibees/elephant/components/cascade-menu';
import { EditEnrichmentDialog } from '@aibees/elephant/feature/enrichment-table/components/edit-enrichment-dialog';
import { useRetryExportMutationApi } from '@aibees/elephant/feature/exports-management-table/api/use-retry-export-mutation-api';
import { ROUTES as R } from '@aibees/elephant/router';
import { EnrichmentTypeEnum } from '@aibees/elephant/types';
import { notificationIconMap } from '@aibees/elephant/utils';
import {
  IconButton,
  PopoverListButton,
  PopoverListButtonProps,
} from '@aibees/shared/components';
import { ExportStatus } from '@aibees/shared/components/status-tag';
import {
  useBoolean,
  useCopyToClipboard,
  useNotify,
} from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils/class-names';
import { Stack } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import type { EnrichmentSearchTableActionProps as Props } from './types';
import { EnrichmentSearchActionType as Action } from './types';

const CASCADE_ITEMS = [
  {
    key: 'include',
    label: 'Include',
    icon: 'add-circle-outlined' as const,
    iconColor: 'primary.500',
  },
  {
    key: 'exclude',
    label: 'Exclude',
    icon: 'stop-sign' as const,
    iconColor: 'error.500',
  },
  {
    key: 'include_and',
    label: 'Include (AND)',
    icon: 'add-task' as const,
    iconColor: 'warning.500',
  },
];

const URL_KEY: Record<string, { company: string; people: string }> = {
  include: {
    company: 'companies_enrichment_request_include',
    people: 'people_enrichment_request_include',
  },
  exclude: {
    company: 'companies_enrichment_request_exclude',
    people: 'people_enrichment_request_exclude',
  },
  include_and: {
    company: 'companies_enrichment_request_all_of',
    people: 'people_enrichment_request_all_of',
  },
};

const ANCHOR_ORIGIN = {
  vertical: 'bottom' as const,
  horizontal: 'right' as const,
};
const TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'right' as const,
};

export const EnrichmentSearchTableAction = memo(
  ({ className, item }: Props) => {
    const notify = useNotify();
    const clip = useCopyToClipboard();
    const editDialogState = useBoolean(false);
    const retryApi = useRetryExportMutationApi({
      id: item.id,
      action: 'enrichment',
    });

    const eyeDisabled =
      item.status === ExportStatus.FAILED ||
      item.status === ExportStatus.PROCESSING;

    const menuItems = useMemo(
      () => [
        {
          label: 'Edit Name',
          key: Action.edit,
          icon: 'edit-outline' as const,
        },
        ...(item.status === ExportStatus.FAILED
          ? [
              {
                label: 'Retry',
                key: Action.retry,
                icon: 'reload' as const,
              },
            ]
          : []),
        {
          label: 'Copy ID',
          key: Action.copyId,
          icon: 'copy' as const,
        },
      ],
      [item.status]
    );

    const handleCascadeSelect = useCallback(
      (parentKey: string) => {
        const subKey =
          item.type === EnrichmentTypeEnum.COMPANY ? 'company' : 'people';
        const urlKey = URL_KEY[parentKey]?.[subKey];
        if (!urlKey) return;
        const route = subKey === 'company' ? R.searchCompany : R.searchPeople;
        const value = `${encodeURIComponent(item.name)}::${encodeURIComponent(
          item.id
        )}`;
        window.open(`${route}?${urlKey}=${value}`, '_blank');
      },
      [item]
    );

    const handleMenuSelect = useCallback<
      NonNullable<PopoverListButtonProps['onSelect']>
    >(
      (e) => {
        if (e.key === Action.edit) {
          editDialogState.onTrue();
        } else if (e.key === Action.copyId) {
          clip.copy(item.id).then(() => {
            notify.complete(
              'ID Copied',
              'The ID has been copied to your clipboard.',
              crypto.randomUUID(),
              { icon: notificationIconMap.Enrich }
            );
          });
        } else if (e.key === Action.retry) {
          retryApi.mutate({});
        }
      },
      [item.id, editDialogState, clip, notify, retryApi]
    );

    const eyeTrigger = useMemo(
      () => (
        <IconButton
          icon="eye-outlined"
          variant="outlined"
          size="xsmall"
          disabled={eyeDisabled}
        />
      ),
      [eyeDisabled]
    );

    return (
      <Stack
        className={classnames('EnrichmentSearchTableAction', className)}
        direction="row"
        alignItems="center"
        justifyContent="center"
        gap={0.5}
      >
        {eyeDisabled ? (
          eyeTrigger
        ) : (
          <CascadeMenu
            items={CASCADE_ITEMS}
            onSelect={handleCascadeSelect}
            buttonElement={eyeTrigger}
          />
        )}

        <PopoverListButton
          icon="more-horizontal"
          variant="outlined"
          size="xsmall"
          anchorOrigin={ANCHOR_ORIGIN}
          transformOrigin={TRANSFORM_ORIGIN}
          items={menuItems}
          itemDivider
          onSelect={handleMenuSelect}
        />

        <EditEnrichmentDialog item={item} state={editDialogState} />
      </Stack>
    );
  }
);

EnrichmentSearchTableAction.displayName = 'EnrichmentSearchTableAction';
